import { useAuth0 } from "@auth0/auth0-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { cx } from "~utils";

import { useEntitlements } from "~/features/access";
import { useAuth } from "~/features/auth";
import { useUser } from "~/features/user";
import { handleTrackClick } from "~/lib/analytics/handleTrackClick";
import { useEnvironment } from "~core/hooks/use-environment";
import { useFlags } from "~core/hooks/use-flags";
import { useSciAmJwtSync } from "~features/piano/hooks/use-piano-auth-sync";

import UserIcon from "~/assets/user";
import styles from "./UserDropdown.module.css";

export function UserDropdown() {
  return (
    <DropdownMenu.Root>
      {/* Button */}
      <DropdownMenu.Trigger className={styles.trigger} aria-label="User Menu">
        <UserDropdownIcon />
      </DropdownMenu.Trigger>

      {/* Dropdown */}
      <DropdownMenu.Content className={styles.content}>
        <DropdownMenu.Group className={styles.group}>
          <DropdownMenu.Arrow className={styles.arrow} />
          {/* User info and account actions */}
          <UserMenuItems />

          {/* Subscribe button shows when user is logged out or doesn't have a paid subscription */}
          <UserSubscribeDropdownItem />

          {/* Error info if applicable - hidden for prod users with default env settings */}
          <DropdownMenu.Label className={styles.label}>
            <UserErrorInfo />
          </DropdownMenu.Label>

          {/* Displays Piano environment (hidden on production) */}
          <UserEnvironment />
        </DropdownMenu.Group>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

function UserDropdownIcon() {
  const { auth } = useEnvironment();
  const slug = auth.environment;
  const { email, firstName, lastName } = useUser();
  // const { hasError, hasWarning } = useAuthSyncError();
  const { dirty } = useHasDirtyEnvironment();

  const isErrorLevelVisible = slug !== "prod" || dirty;
  const errorLevel = ""; //hasError ? "error" : hasWarning ? "warn" : "";
  const showErrorState = false; // hasError || (isErrorLevelVisible && hasWarning);

  const initials =
    firstName && lastName
      ? // First letter of first name and first letter of last name
        `${firstName[0]}${lastName[0]}`
      : // First letter of email
        email?.[0] || "";

  return (
    <div
      className={cx(
        styles.dropdownIconContainer,
        auth.environment && styles[auth.environment],
        showErrorState && styles["has-error"],
        isErrorLevelVisible && styles[errorLevel],
        initials && styles.showInitials,
      )}
      aria-label="User menu"
    >
      <UserIcon className={cx(styles.dropdownIcon, styles.profileIconImg)} />
      <div className={cx(styles.dropdownIcon, styles.initials)}>
        {showErrorState ? "!" : initials}
      </div>
    </div>
  );
}

function UserMenuItems() {
  const { userId } = useUser();
  const { logout, login } = useAuth();

  return userId ? (
    <>
      {/* User info and subscription info */}
      <DropdownMenu.Label className={styles.label}>
        <UserInfoHeading />
        <UserSubscriptionInfo />
      </DropdownMenu.Label>
      <DropdownMenu.Separator className={styles.separator} />

      {/* User Account Actions */}
      <DropdownMenu.Item className={styles.item} asChild>
        <a href="/account/">My Account</a>
      </DropdownMenu.Item>
      <DropdownMenu.Item className={styles.item} asChild>
        <a href="/newsletters/">Newsletters</a>
      </DropdownMenu.Item>
      <DropdownMenu.Item className={styles.item} onSelect={logout}>
        Sign Out
      </DropdownMenu.Item>
    </>
  ) : (
    <>
      <DropdownMenu.Item className={styles.item} onSelect={login}>
        Sign In
      </DropdownMenu.Item>
      <DropdownMenu.Item className={styles.item} asChild>
        <a href="/newsletters/">Newsletters</a>
      </DropdownMenu.Item>
    </>
  );
}

function UserInfoHeading() {
  const user = useUser();

  return (
    <div className={styles.userInfo}>
      <div className={styles.userName}>
        {/* It's not guaranteed that all users will have a first and last name */}
        {user?.firstName && user.lastName
          ? `${user.firstName} ${user.lastName}`
          : user?.name || user?.email}
      </div>
    </div>
  );
}

function UserSubscriptionInfo() {
  // Base subscription info on the user's plan and entitlements
  const { primaryActivePlan, isActiveSubscriber } = useUser();
  const { hasSub } = useEntitlements();

  /** The user's current subscription */
  let activeSubscription = "";
  /** Reason why the user's subscription isn't available */
  let errorStatus = "";
  let errorDetail = "";

  if (hasSub) activeSubscription = "Subscriber";

  if (primaryActivePlan?.external_name) {
    activeSubscription = primaryActivePlan.external_name + " Subscription";
  }

  // When the user's subscription isn't available, show a message indicating why
  const { isLoggedIn, isLoading, login } = useAuth();
  const { ready, stale, payload, updating } = useSciAmJwtSync();
  const idle = ready && !updating;

  // If the user is logged in but we're still loading their subscription info
  if (!idle) errorStatus = "Loading Subscription...";

  // If the SciAm JWT is stale and the user is not logged in, we can assume the user's
  // Auth0 session expired and they need to log in again.
  const authSessionExpired = !!payload && !!stale && !updating && !isLoading && !isLoggedIn;
  if (authSessionExpired) errorStatus = "Problem updating your account.";

  // If the user should have a SciAm JWT but doesn't, or if Django returns a null features object,
  // we can assume there's a problem with the user's subscription.
  const shouldHaveSciAmJwt = isLoggedIn && idle;
  const hasJwtEntitlements = !!payload?.features;
  if (shouldHaveSciAmJwt && !hasJwtEntitlements) {
    errorStatus = "Subscription unavailable";

    // If the stale flag is set to "warn", we know there's an ongoing problem between Django and Chargebee
    if (stale === "warn" || isActiveSubscriber) {
      errorDetail =
        "Our system is under maintenance. Your subscription info will be available soon.";
    }
  }

  return (
    <div className={cx(styles.subscription, (!ready || updating) && styles.loading)}>
      <div className={styles.name}>
        {activeSubscription || errorStatus || "No Active Subscription"}
      </div>

      {/* {errorDetail && <div className={styles.errorDetail}>{errorDetail}</div>} */}

      {/* Convenience link for users with borked Auth0 sessions */}
      {ready && authSessionExpired && (
        <div className={styles.detail}>
          <a href="/login/" onClick={login} className={styles.reauth}>
            {/* Tweak the language based on whether we know the user has an active sub */}
            {hasSub ? "Sign in again to update your subscription." : "Please sign in again."}
          </a>
        </div>
      )}
    </div>
  );
}

/**
 *
 * @param {object} params
 * @param {string} params.className
 * @returns
 */
function UserErrorInfo({ className }) {
  const errors = [];
  const mainError = errors.find((e) => e.level === "error") || errors[0];

  const { auth } = useEnvironment();
  const slug = auth.environment;
  const { dirty } = useHasDirtyEnvironment();

  // Regular users will never see this
  if ((slug === "prod" && !dirty) || !errors.length) {
    return null;
  }

  return (
    <>
      <DropdownMenu.Separator className={cx(styles.separator, styles.sub)} />
      <div className={cx(styles.errorInfo, className)}>
        <div className={cx(styles.message, mainError?.level && styles[mainError?.level])}>
          {mainError?.userMessage || "An error occurred while loading your subscription info"} (
          {errors.map((e) => e.code).join(", ")})
        </div>
        <div className={styles.troubleshooting}>{mainError?.troubleshooting}</div>
      </div>
    </>
  );
}

function UserSubscribeDropdownItem() {
  const { hasSub } = useEntitlements();

  function handleSubscribeClick() {
    return; // @TODO: Update with correct tracking event
    handleTrackClick("site-header-getsciam-button-3");
  }

  return (
    !hasSub && (
      <DropdownMenu.Item className={styles.item} asChild>
        <a
          href="/getsciam/?utm_source=site&utm_medium=display&utm_term=menu_button"
          onClick={handleSubscribeClick}
        >
          Subscribe
        </a>
      </DropdownMenu.Item>
    )
  );
}

/**
 * A "dirty" environment means the user is overriding at least one default setting
 * e.g., using Piano Production on a staging environment
 */
function useHasDirtyEnvironment() {
  const flags = useFlags();
  const hasFlags = flags && Object.keys(flags).length > 0;
  const hasEnvFlags = !!flags?.auth0 || !!flags?.chargebee;

  return {
    hasFlags,
    hasEnvFlags,
    dirty: hasFlags,
  };
}

function UserEnvironment() {
  const { auth } = useEnvironment();
  const slug = auth.environment;
  const { dirty, hasFlags, hasEnvFlags } = useHasDirtyEnvironment();
  const nonProdEnv = slug !== "prod";

  // Regular users will never see this
  if (!nonProdEnv && !dirty) {
    return null;
  }

  return (
    <div className={cx(styles.userEnv, styles[slug])}>
      {/* Show the user's current environment */}
      <div className={styles.name}>Environment</div>
      <div className={styles.detail}>
        <UserEnvTable />
      </div>

      {/* Show the user's flags (if they have any) */}
      {hasFlags && (
        <>
          <DropdownMenu.Separator className={styles.separator} />
          <div className={styles.name}>Flags</div>
          <div className={styles.detail}>
            <UserFlagTable />
          </div>
        </>
      )}

      <UserAuth0State />

      {/* Let the user reset all environment flags with one click */}
      {dirty && (
        <DropdownMenu.Item className={cx(styles.item, styles.danger)} asChild>
          <a href="?flag=reset">
            Reset {hasEnvFlags ? "Environment & Feature Flags" : "Feature Flags"}
          </a>
        </DropdownMenu.Item>
      )}
    </div>
  );
}

function UserEnvTable() {
  const { auth, entitlements } = useEnvironment();

  return (
    <table className={styles.table}>
      <tbody>
        <tr>
          <td>Authentication</td>
          <td>
            {auth.provider} • {auth.environment}
          </td>
        </tr>
        <tr>
          <td>Entitlements</td>
          <td>
            {entitlements.provider} • {entitlements.environment}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

function UserFlagTable() {
  const flags = useFlags();

  return (
    <table className={cx(styles.table, styles.mono)}>
      <tbody>
        {Object.entries(flags).map(([flag, value]) => (
          <tr key={flag}>
            <td>{flag}</td>
            <td>{value.toString()}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function UserAuth0State() {
  const { isAuthenticated, isLoading, user, error } = useAuth0();

  return (
    <div>
      <DropdownMenu.Separator className={styles.separator} />
      <div className={styles.name}>Auth0</div>
      <div className={styles.detail}>
        <table className={cx(styles.table, styles.mono)}>
          <tbody>
            <tr>
              <td>isAuthenticated</td>
              <td>{isAuthenticated.toString()}</td>
            </tr>
            <tr>
              <td>isLoading</td>
              <td>{isLoading.toString()}</td>
            </tr>
            {error && (
              <tr>
                <td>error</td>
                <td>{JSON.stringify(error)}</td>
              </tr>
            )}
            {Object.entries(user || {}).map(([key, value]) => (
              <tr key={key}>
                <td>{key}</td>
                <td
                  style={{
                    maxWidth: "40ch",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}
                >
                  {JSON.stringify(value)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default UserDropdown;
